import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CircularProgress from '@mui/material/CircularProgress';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { styled } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import GetAppIcon from '@mui/icons-material/GetApp';
import CalculateIcon from '@mui/icons-material/Calculate';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase } from '../utils/supabaseClient';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Tooltip from '@mui/material/Tooltip';
import { useAuth } from '../context/AuthContext';
import { TextField } from '@mui/material';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import SortableTable from '../components/SortableTable';

// Helper function for consistent date handling
const getAdjustedDate = (dateInput) => {
  // Handle if input is already a Date object
  if (dateInput instanceof Date) {
    const date = new Date(dateInput);
    date.setUTCHours(12, 0, 0, 0);
    return date;
  }
  
  // Handle string input
  if (typeof dateInput === 'string') {
    // If it's already an ISO string with time, use it as is
    if (dateInput.includes('T')) {
      return new Date(dateInput);
    }
    // Otherwise append noon UTC time to ensure date consistency
    return new Date(`${dateInput}T12:00:00Z`);
  }
  
  // Return null for undefined or null input
  return null;
};

// Styled components for the file upload
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function UploadData() {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploads, setUploads] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [workWeekDialogOpen, setWorkWeekDialogOpen] = useState(false);
  const [workWeekFromDate, setWorkWeekFromDate] = useState(null);
  const [workWeekToDate, setWorkWeekToDate] = useState(null);
  const [customFileName, setCustomFileName] = useState('');
  const [uploadId, setUploadId] = useState(null);
  const [dateError, setDateError] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    fetchUploads();
  }, []);

  async function fetchUploads() {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('uploads')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) {
        throw error;
      }
      
      setUploads(data || []);
    } catch (error) {
      console.error('Error fetching uploads:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
          file.type === 'application/vnd.ms-excel' ||
          file.type === 'text/csv') {
        setSelectedFile(file);
        setUploadError('');
      } else {
        setSelectedFile(null);
        setUploadError('Please select an Excel or CSV file');
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadError('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');
      setUploadSuccess(false);
      
      // 1. Upload file to Supabase Storage
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2, 15)}_${Date.now()}.${fileExt}`;
      const filePath = `uploads/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, selectedFile);

      if (uploadError) {
        console.error("Storage upload error:", uploadError);
        throw uploadError;
      }
      
      console.log("File uploaded to storage successfully");

      // 2. Create a record in the uploads table with the correct schema and user info
      const { data, error } = await supabase
        .from('uploads')
        .insert([
          {
            filename: selectedFile.name,
            original_filename: selectedFile.name,
            file_path: filePath,
            file_size: selectedFile.size,
            status: 'pending',
            user_id: user?.id,
            uploaded_by: user?.email
          }
        ])
        .select();

      console.log("Database insert response:", data, error);
      
      if (error) {
        throw error;
      }

      setUploadSuccess(true);
      
      // Store the upload ID for potential work week addition
      if (data && data.length > 0) {
        setUploadId(data[0].id);
        // Show the work week dialog
        setWorkWeekDialogOpen(true);
      }
      
      setSelectedFile(null);
      
      // Clear the file input
      const fileInput = document.getElementById('upload-file');
      if (fileInput) fileInput.value = '';
      
      // Refresh the uploads list
      fetchUploads();
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError(`Error uploading file: ${error.message || JSON.stringify(error)}`);
      setUploading(false);
    }
  };

  const handleWorkWeekSave = async () => {
    // Validate dates
    if (!workWeekFromDate || !workWeekToDate) {
      setDateError('Both From and To Dates are required.');
      return;
    }

    // Check that To Date is not earlier than From Date
    if (workWeekToDate < workWeekFromDate) {
      setDateError('To Date cannot be earlier than From Date.');
      return;
    }

    setDateError('');

    try {
      // Create adjusted dates with noon UTC time to prevent timezone shifting issues
      const adjustedFromDate = getAdjustedDate(workWeekFromDate);
      const adjustedToDate = getAdjustedDate(workWeekToDate);
      
      // Update the upload record with work week dates and custom file name
      const { error } = await supabase
        .from('uploads')
        .update({
          work_week_from_date: adjustedFromDate.toISOString(),
          work_week_to_date: adjustedToDate.toISOString(),
          filename: customFileName || uploads.find(u => u.id === uploadId)?.original_filename || uploads.find(u => u.id === uploadId)?.filename
        })
        .eq('id', uploadId);

      if (error) {
        throw error;
      }

      // Close dialog and refresh list
      setWorkWeekDialogOpen(false);
      fetchUploads();
    } catch (error) {
      console.error('Error saving work week:', error);
      setDateError(`Error saving work week: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleWorkWeekCancel = () => {
    setWorkWeekDialogOpen(false);
    setWorkWeekFromDate(null);
    setWorkWeekToDate(null);
    setCustomFileName('');
    setDateError('');
    setUploading(false);
  };

  const handleAddWorkWeekClick = (upload) => {
    setUploadId(upload.id);
    setCustomFileName(upload.filename !== upload.original_filename ? upload.filename : '');
    setWorkWeekDialogOpen(true);
  };

  const handleDeleteClick = (upload) => {
    setFileToDelete(upload);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    
    try {
      setLoading(true);
      
      // 1. Delete the file from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([fileToDelete.file_path]);
        
      if (storageError) {
        console.error('Error deleting file from storage:', storageError);
        // Continue anyway to delete the database record
      }
      
      // 2. Delete the record from the database
      const { error: dbError } = await supabase
        .from('uploads')
        .delete()
        .eq('id', fileToDelete.id);
        
      if (dbError) {
        throw dbError;
      }
      
      // Refresh the uploads list
      fetchUploads();
    } catch (error) {
      console.error('Error deleting upload:', error);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    }
  };

  const handleCalculateClick = (upload) => {
    navigate(`/calculate/${upload.id}`);
  };

  const handleDownload = async (upload) => {
    try {
      const { data, error } = await supabase.storage
        .from('documents')
        .download(upload.file_path);
        
      if (error) {
        throw error;
      }
      
      // Create a download link and trigger the download
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = upload.filename;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MM/dd/yyyy h:mm a');
    } catch (error) {
      return dateString;
    }
  };

  const getStatusChip = (status) => {
    let color = 'default';
    
    switch (status) {
      case 'pending':
        color = 'info';
        break;
      case 'processed':
        color = 'success';
        break;
      case 'error':
        color = 'error';
        break;
      default:
        color = 'default';
    }
    
    return (
      <Chip 
        label={status} 
        color={color} 
        size="small" 
        variant="outlined"
      />
    );
  };

  const getWorkWeekText = (upload) => {
    if (upload.work_week_from_date && upload.work_week_to_date) {
      // Use date-fns parseISO to better handle ISO date strings
      const fromDate = getAdjustedDate(upload.work_week_from_date);
      const toDate = getAdjustedDate(upload.work_week_to_date);
      
      // Use UTC methods to get consistent dates regardless of local timezone
      return `${format(fromDate, 'MM/dd/yyyy')} - ${format(toDate, 'MM/dd/yyyy')}`;
    }
    return 'Not set';
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Upload Data File
        </Typography>
        
        <Typography paragraph>
          Upload an Excel or CSV file containing the placement data for calculation.
        </Typography>
        
        {uploadSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            File uploaded successfully!
          </Alert>
        )}
        
        {uploadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {uploadError}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button 
            component="label" 
            variant="outlined" 
            startIcon={<UploadFileIcon />}
          >
            Choose File
            <input
              id="upload-file"
              hidden
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
            />
          </Button>
          
          <Button 
            variant="contained" 
            startIcon={<CloudUploadIcon />}
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
            {uploading && <CircularProgress size={24} sx={{ ml: 1 }} />}
          </Button>
          
          {selectedFile && (
            <Typography variant="body2">
              Selected file: {selectedFile.name}
            </Typography>
          )}
        </Box>
      </Paper>
      
      <Paper sx={{ mt: 3, p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Uploaded Files
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : uploads.length > 0 ? (
          <SortableTable
            rows={uploads}
            headCells={[
              { id: 'filename', label: 'Filename', align: 'left' },
              { id: 'created_at', label: 'Upload Date', align: 'left' },
              { id: 'file_size', label: 'Size', align: 'left' },
              { id: 'status', label: 'Status', align: 'left' },
              { id: 'uploaded_by', label: 'Uploaded By', align: 'left' },
              { id: 'work_week', label: 'Work Week', align: 'left', disableSorting: true },
              { id: 'actions', label: 'Actions', align: 'left', disableSorting: true },
            ]}
            defaultOrderBy="created_at"
            defaultOrder="desc"
            tableId="uploads-table"
            defaultRowsPerPage={25}
            renderRow={(upload) => (
              <TableRow key={upload.id}>
                <TableCell>{upload.filename}</TableCell>
                <TableCell>{formatDate(upload.created_at)}</TableCell>
                <TableCell>{formatFileSize(upload.file_size)}</TableCell>
                <TableCell>{getStatusChip(upload.status)}</TableCell>
                <TableCell>{upload.uploaded_by || 'Unknown'}</TableCell>
                <TableCell>
                  {getWorkWeekText(upload)}
                  <IconButton 
                    size="small" 
                    onClick={() => handleAddWorkWeekClick(upload)}
                    sx={{ ml: 1 }}
                  >
                    <DateRangeIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell>
                  {(upload.status === 'completed' || upload.status === 'processed') && (
                    <Tooltip title="View Results">
                      <IconButton 
                        size="small"
                        color="primary"
                        onClick={() => navigate(`/results/${upload.id}?refusal=${upload.file_path.includes('/refusal/')}`)}
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  <Tooltip title="Download">
                    <IconButton 
                      size="small"
                      onClick={() => handleDownload(upload)}
                    >
                      <GetAppIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Calculate">
                    <IconButton 
                      size="small" 
                      color="secondary"
                      onClick={() => handleCalculateClick(upload)}
                    >
                      <CalculateIcon />
                    </IconButton>
                  </Tooltip>
                  
                  <Tooltip title="Delete">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleDeleteClick(upload)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            )}
            emptyContent={
              <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
                No files uploaded yet.
              </Typography>
            }
          />
        ) : (
          <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
            No files uploaded yet.
          </Typography>
        )}
      </Paper>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this file? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Work Week Dialog */}
      <Dialog
        open={workWeekDialogOpen}
        onClose={handleWorkWeekCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Optional Upload Info</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Add optional information for this upload.
          </DialogContentText>
          
          {dateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {dateError}
            </Alert>
          )}
          
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 'bold', 
              mt: 1, 
              mb: 1,
              borderBottom: '1px solid #e0e0e0',
              pb: 1
            }}
          >
            File Information
          </Typography>
          
          <TextField
            label="Custom File Name"
            value={customFileName}
            onChange={(e) => setCustomFileName(e.target.value)}
            fullWidth
            margin="normal"
            placeholder="Enter a custom file name (optional)"
          />
          
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 'bold', 
              mt: 3, 
              mb: 1,
              borderBottom: '1px solid #e0e0e0',
              pb: 1
            }}
          >
            Work Week Date Range
          </Typography>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <DatePicker
                label="From Date"
                value={workWeekFromDate}
                onChange={(newValue) => setWorkWeekFromDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
              
              <DatePicker
                label="To Date"
                value={workWeekToDate}
                onChange={(newValue) => setWorkWeekToDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleWorkWeekCancel}>Cancel</Button>
          <Button 
            onClick={handleWorkWeekSave} 
            color="primary" 
            variant="contained"
            disabled={!workWeekFromDate || !workWeekToDate}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UploadData; 